import React from 'react';
import { Box, Typography, Paper, Grid, Chip, Divider, alpha, useTheme } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutlineRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ApartmentIcon from '@mui/icons-material/ApartmentRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DomainIcon from '@mui/icons-material/DomainRounded';
import PersonIcon from '@mui/icons-material/PersonRounded';
import SchoolIcon from '@mui/icons-material/SchoolRounded';
import { motion } from 'framer-motion';
import { getStudentUser } from '../api/studentApi';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp } from '../utils/motion';

const MotionPaper = motion(Paper);
const MotionChip = motion(Chip);

const InfoRow = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Box sx={{ color: 'primary.main', mt: 0.3 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
      </Box>
    </Box>
  </Grid>
);

// Pulled straight from the login response's `user` object stored in sessionStorage --
// no extra API call needed unless a richer profile endpoint is added later.
const Profile = () => {
  const theme = useTheme();
  const user = getStudentUser();
  const admission = user?.admission_details || {};
  const initials = (admission.student_name || user?.employee_name || 'S').charAt(0).toUpperCase();

  return (
    <Box component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
      <Typography variant="h5" sx={{ mb: 2 }}>My profile</Typography>

      <MotionPaper
        variants={fadeInUp}
        elevation={0}
        sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}
      >
        <Box
          sx={{
            height: 92,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${tokens.violetDeep})`,
          }}
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Box
            sx={{
              width: 76, height: 76, borderRadius: '20px', mt: '-38px', mb: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.6rem', fontWeight: 800, color: '#fff',
              border: '4px solid', borderColor: 'background.paper',
              background: `linear-gradient(135deg, ${tokens.amber}, ${theme.palette.primary.main})`,
              fontFamily: 'var(--font-display)',
            }}
          >
            {initials}
          </Box>
          <Typography variant="h6">{admission.student_name || user?.employee_name || 'Student'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {admission.admission_no ? `Admission No. ${admission.admission_no}` : 'Student'}
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          <Grid container spacing={2.5}>
            <InfoRow icon={<PersonIcon fontSize="small" />} label="Student name" value={admission.student_name} />
            <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Admission no." value={admission.admission_no} />
            <InfoRow icon={<MailOutlineIcon fontSize="small" />} label="Email" value={admission.email} />
            <InfoRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Mobile no." value={admission.mobile_no} />
            <InfoRow icon={<ApartmentIcon fontSize="small" />} label="Branch" value={admission.branch_name} />
            <InfoRow icon={<DomainIcon fontSize="small" />} label="Organization" value={admission.organization_name} />
          </Grid>
        </Box>
      </MotionPaper>

      <MotionPaper variants={fadeInUp} elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon fontSize="small" color="primary" /> Enrolled courses
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(user?.courses || []).map((c, i) => (
            <MotionChip
              key={c.course_id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 * i, duration: 0.3 }}
              label={`${c.course_name} — ${c.batch_name}`}
              sx={{ px: 0.5, py: 2.2, backgroundColor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
            />
          ))}
          {(!user?.courses || user.courses.length === 0) && (
            <Typography variant="body2" color="text.secondary">No courses enrolled.</Typography>
          )}
        </Box>
      </MotionPaper>
    </Box>
  );
};

export default Profile;
